import {
  RolodexEntryJson,
} from 'types/rolodex/rolodexEntries'

import { GeckoEntryBase } from './geckoEntry'

export enum GeneratedTimelineCategory {
  Inspection = 'inspection',
  Risk = 'risk',
  Repair = 'repair',
  DataChange = 'data-change',
  Document = 'document',
}

const GENERATED_TIMELINE_CATEGORY_VALUES: ReadonlySet<string> = new Set(
  Object.values(GeneratedTimelineCategory)
)

export interface GeneratedTimelineImageRef {
  name: string
  url: string
  caption?: string
}

/**
 * One pre-built event surfaced in the document timeline. `documents` lists
 * GeckoReport entry IDs that back the event; `images` are inline image refs.
 */
export interface GeneratedTimelineEvent {
  id: string
  category: GeneratedTimelineCategory
  title: string
  date?: string
  description?: string
  summary?: string
  badge: string
  documents?: string[]
  images?: GeneratedTimelineImageRef[]
}

export type DocumentTimelineEntryData = {
  generated_timeline?: GeneratedTimelineEvent[]
}

function parseGeneratedTimeline(
  value: unknown
): GeneratedTimelineEvent[] | undefined {
  if (!Array.isArray(value)) return undefined
  return value
    .filter(
      (entry): entry is Omit<GeneratedTimelineEvent, 'category'> & {
        category: string
      } => {
        if (!entry || typeof entry !== 'object') return false
        const e = entry as { [k: string]: unknown }
        return (
          typeof e.id === 'string' &&
          typeof e.title === 'string' &&
          typeof e.category === 'string' &&
          typeof e.badge === 'string' &&
          (e.date === undefined ||
            (typeof e.date === 'string' && (e.date as string).length > 0)) &&
          (e.documents === undefined || Array.isArray(e.documents)) &&
          (e.images === undefined || Array.isArray(e.images))
        )
      }
    )
    .map((e) => ({
      ...e,
      category: GENERATED_TIMELINE_CATEGORY_VALUES.has(e.category)
        ? (e.category as GeneratedTimelineCategory)
        : GeneratedTimelineCategory.Document,
    }))
}

export class GeckoDocumentTimelineEntry extends GeckoEntryBase<
  DocumentTimelineEntryData,
  {
    unit?: string
  }
> {
  private constructor(json: RolodexEntryJson) {
    super(
      json,
      {
        generated_timeline: parseGeneratedTimeline(
          (json.data as { generated_timeline?: unknown })?.generated_timeline
        ),
      },
      {
        unit: json.tags.unit as string | undefined,
      },
      {}
    )
  }

  public static CreateFromJSON(json: RolodexEntryJson): GeckoDocumentTimelineEntry {
    return new GeckoDocumentTimelineEntry(json)
  }
}
